import { useCallback, useEffect, useRef, useState } from 'react';
import {
  disconnectInstagram, getInstagramAuthorizationUrl, getInstagramConnection,
  refreshInstagramConnection, selectInstagramAccount, type InstagramSnapshot,
} from '../services/instagram.ts';

export function useInstagramConnection() {
  const [snapshot, setSnapshot] = useState<InstagramSnapshot>({ available: false, connection: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(false);
  const generation = useRef(0);
  const operation = useRef(false);
  const currentSnapshot = useRef(snapshot);
  currentSnapshot.current = snapshot;

  const reload = useCallback(async () => {
    if (operation.current) return;
    const request = ++generation.current;
    setLoading(true);
    try {
      const data = await getInstagramConnection();
      if (mounted.current && request === generation.current) {
        if (!data.available && !data.connection && currentSnapshot.current.connection) {
          setSnapshot({ ...currentSnapshot.current, available: false });
          setError('Não foi possível verificar o Instagram. A configuração foi preservada.');
        } else {
          setSnapshot(data);
          setError('');
        }
      }
    } catch (failure: any) {
      if (mounted.current && request === generation.current) {
        // O frontend pode ser entregue antes da ativação do backend compartilhado.
        // Não interpretar falha de consulta como desconexão de uma conta conhecida.
        if (failure?.response?.status === 404 && !currentSnapshot.current.connection) {
          setSnapshot({ available: false, connection: null });
        } else {
          setError('Não foi possível consultar o Instagram. Tente novamente.');
        }
      }
    } finally {
      if (mounted.current && request === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void reload();
    return () => { mounted.current = false; generation.current += 1; };
  }, [reload]);

  const run = useCallback(async (action: () => Promise<InstagramSnapshot>, message: string) => {
    if (operation.current) return false;
    operation.current = true;
    ++generation.current;
    setLoading(false);
    setBusy(true);
    setError('');
    try {
      const data = await action();
      if (mounted.current) setSnapshot(data);
      return true;
    } catch {
      if (mounted.current) setError(message);
      return false;
    } finally {
      operation.current = false;
      if (mounted.current) setBusy(false);
    }
  }, []);

  const connect = useCallback(async () => {
    if (operation.current) return;
    operation.current = true;
    ++generation.current;
    setLoading(false);
    setBusy(true);
    setError('');
    try {
      const url = await getInstagramAuthorizationUrl();
      if (mounted.current) window.location.assign(url);
    } catch {
      if (mounted.current) setError('Não foi possível iniciar a autorização do Instagram. Tente novamente.');
    } finally {
      operation.current = false;
      if (mounted.current) setBusy(false);
    }
  }, []);

  return {
    ...snapshot, loading, busy, error, reload, connect,
    refresh: () => run(refreshInstagramConnection, 'Não foi possível atualizar o Instagram. A conexão foi preservada.'),
    select: (id: string) => run(() => selectInstagramAccount(id), 'Não foi possível conectar esse perfil. Confira a autorização.'),
    disconnect: () => run(disconnectInstagram, 'Não foi possível confirmar a desconexão. Atualize o estado antes de tentar novamente.'),
  };
}
